import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import useDebounce from "@/context/useDebounce";
import { useGlobalStore } from "@/context/useGlobalStore";
import {
    ROUTE_QUERY_GC_TIME,
    ROUTE_QUERY_STALE_TIME,
    keepPreviousQueryData,
} from "@/hooks/common/queryDefaults";

const EMPTY_LIST = [];

/**
 * Factory hook for searchable antd Select components backed by the global store cache.
 *
 * @param {object}   config
 * @param {string}   config.storeKey     - key in useGlobalStore to cache and read items
 * @param {string[]} config.queryKey     - React Query base key (search + filter are appended)
 * @param {Function} config.queryFn      - (search: string, filter: object) => Promise<Item[]>
 *                                         Must resolve to a plain array of items.
 * @param {Function} config.toOption     - (item) => { value, label, ...rest }
 * @param {object}   [config.filter]     - extra filter params forwarded to queryFn
 * @param {boolean}  [config.activeFirst] - prepend a "Select …" null option
 * @param {string}   [config.placeholder]
 * @param {Function} [config.onChange]
 */
export function useSelectOptions({
    storeKey,
    queryKey,
    queryFn,
    toOption,
    filter = {},
    activeFirst = false,
    placeholder = "Select",
    onChange,
}) {
    const setStateSmart = useGlobalStore((s) => s.setStateSmart);
    const list = useGlobalStore(useCallback((s) => s[storeKey] ?? EMPTY_LIST, [storeKey]));

    const [search, setSearch] = useState("");
    const debounced = useDebounce(search);

    const { data, isFetching } = useQuery({
        queryKey: [...queryKey, debounced, filter],
        queryFn: () => queryFn(debounced, filter),
        staleTime: ROUTE_QUERY_STALE_TIME,
        gcTime: ROUTE_QUERY_GC_TIME,
        placeholderData: keepPreviousQueryData,
    });

    useEffect(() => {
        if (Array.isArray(data) && data.length) {
            setStateSmart(storeKey, data, "id");
        }
    }, [data, setStateSmart, storeKey]);

    return useMemo(
        () => ({
            showSearch: true,
            onSearch: setSearch,
            filterOption: (input, option) =>
                String(option?.label ?? "")
                    .toLowerCase()
                    .includes(String(input).toLowerCase()),
            allowClear: true,
            defaultActiveFirstOption: activeFirst,
            loading: isFetching,
            placeholder,
            onChange,
            options: [
                ...(activeFirst ? [{ label: placeholder, value: null }] : []),
                ...(Array.isArray(list) ? list : EMPTY_LIST).map(toOption),
            ],
        }),
        [list, isFetching, activeFirst, onChange, placeholder, toOption]
    );
}
